import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelSearchPageFilters from 'components/Pages/HotelSearchPage/HotelSearchPageFilters'
import OfferList from 'components/OfferList/OfferList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE,
} from 'constants/offer'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import styled from 'styled-components'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { LUXURY_ESCAPES } from 'constants/brands'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import useProductLabels from 'hooks/useProductLabels'
import productLabels from 'i18n/productLabels'

const LuxBundlesVertical = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const OffersSection = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};
`

const HeroContent = styled(LayoutContainer)`
  display: flex;
  height: 100%;
  width: 100%;
  color: ${props => props.theme.palette.neutral.default.eight};
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
`

const LuxBundlesImageBanner = styled.div`
  height: ${rem(200)};
  position: relative;
  background-color: ${props => props.theme.palette.brand.primary.normal};

  ${mediaQueryUp.tablet} {
    height: ${rem(300)};
  }
`

interface Props {
  filters: App.OfferListFilters
  regionCode: string
}

function LuxBundlesPage(props: Props) {
  const { filters, regionCode } = props
  const intl = useIntl()
  const { luxBundleLabel } = useProductLabels()
  const { frontPageSortByTypes } = useOfferListSortOptions()

  const dispatch = useAppDispatch()

  const headTitle = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: 'eNWUV6Y1pd4PGjp8caKl',
        defaultMessage: 'Luxury Hotels & Resorts at the Best Prices on Earth',
        description: 'Page title for luxury bundles page on the hotel or room booking page',
      })
    : intl.formatMessage(productLabels.hotelsAndResorts)
  const description = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: 'dxGyRhqdDv6twV8bFHRp',
        defaultMessage: 'Book exclusive special offers for luxury hotels and accommodation at insider prices. Best Price Guarantee.',
        description: 'Meta description for luxury bundles page on the hotel page',
      })
    : undefined

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const finalFilters = useMemo((): App.OfferListFilters => {
    return {
      ...filters,
      offerTypes: [OFFER_TYPE_BUNDLE_AND_SAVE],
      frontPageVisibleOnly: true,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters])

  return <LuxBundlesVertical>
    <HeadData
      title={intl.formatMessage(
        {
          id: 'LhIRIuNqDZ726Jd4Fxco',
          defaultMessage: '{headTitle} - {configTitle} {regionCode}',
          description: 'Page title for lux bundles page on the offer types landing page',
        },
        { headTitle, configTitle: config.title, regionCode },
      )}
      description={description}
    />
    <ErrorBoundary fallback={null}>
      <PromptCardSelector tripReengagement />
    </ErrorBoundary>
    <LuxBundlesImageBanner>
      <HeroContent>
        <Group direction="vertical" horizontalAlign="center" gap={24}>
          <Heading variant="heading2" format="titlecase" as="h2" align="center" colour="highlight-primary">
            {intl.formatMessage(
              {
                id: 'DVOoQMh45TFSsMqmeWcS',
                defaultMessage: '{label}s',
                description: 'Pluralized bundle label for page heading on the offer types landing page',
              },
              {
                label: luxBundleLabel,
              },
            )}
          </Heading>
          <Heading variant="heading3" colour="neutral-eight" align="center">
            {intl.formatMessage({
              id: 'YzmMh4Ija1Ho-rVOPQFg',
              defaultMessage: 'Some catchy subtitle would go here to explain this vertical',
              description: 'Placeholder subtitle for lux bundles page hero section',
            })}
          </Heading>
        </Group>
      </HeroContent>
    </LuxBundlesImageBanner>
    <LayoutContainer relative>
      <OffersSection id="offers" gap={40}>
        <div>
          <HotelSearchPageFilters
            heading={intl.formatMessage({
              id: 'MlXDwVuV5yoMBs9huy9s',
              defaultMessage: 'Explore our bundles',
              description: 'Section heading for the bundles offer list filter section',
            })}
            filters={finalFilters}
            sortOptions={frontPageSortByTypes}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
          />
        </div>
        <OfferList
          filters={finalFilters}
          interstitials={[{
            id: 'noResults',
            show: 'hasNoOffers',
            position: 'start',
            element: <VerticalSpacer gap={40}>
              <Heading variant="heading4">{intl.formatMessage(
                {
                  id: 'vlcNmoMX9T1InYsWSW9r',
                  defaultMessage: 'Unfortunately, there are no {bundleLabel} hotels available at this time.',
                  description: 'No results heading when no bundle hotels are available',
                },
                { bundleLabel: luxBundleLabel },
              )}
              </Heading>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'Xi6OJZxxrbyCnzKPZfoI',
                  defaultMessage: 'Check out our other hotels collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.',
                  description: 'No results body text suggesting alternative hotel options',
                })}
              </BodyText>
              <TextButton kind="primary" to="">
                {intl.formatMessage({
                  id: 'OEbehp9lNejUEpXD0-6B',
                  defaultMessage: 'View more hotels options',
                  description: 'Button to view more hotel options when no bundles available',
                })}
              </TextButton>
            </VerticalSpacer>,
          }]}
        />
      </OffersSection>
    </LayoutContainer>
  </LuxBundlesVertical>
}

function mapStateToProps(state: App.State) {
  return {
    filters: getUrlOfferListFilters(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(LuxBundlesPage)
